import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Clock, Flag, ChevronLeft, ChevronRight, 
  Loader2, AlertTriangle, CheckCircle, Send,
  Bookmark, BookmarkCheck, Trophy, XCircle,
  BarChart3, RefreshCw
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const TestAttemptPage = () => {
  const { testId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Test state
  const [test, setTest] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [startTime, setStartTime] = useState(null);
  
  // Results state
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Fetch test and start attempt
  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    startAttempt();
  }, [user, token, testId, navigate]);

  // Timer
  useEffect(() => {
    if (timeRemaining <= 0 || showResults) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true); // Auto-submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, showResults]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (showResults || !attempt) return;

    const saveInterval = setInterval(() => {
      saveProgress();
    }, 30000);

    return () => clearInterval(saveInterval);
  }, [answers, markedForReview, attempt, showResults]);

  const startAttempt = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/structured-tests/${testId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to start test');
      
      const data = await res.json();
      setTest({
        test_id: data.test_id,
        title: data.title,
        questions: data.questions,
        duration_minutes: data.duration_minutes
      });
      setAttempt({ attempt_id: data.attempt_id });
      setAnswers(data.saved_answers || {});
      setMarkedForReview(data.marked_for_review || []);
      
      // Calculate remaining time
      if (data.resumed) {
        const startedAt = new Date(data.started_at);
        const elapsed = Math.floor((Date.now() - startedAt.getTime()) / 1000);
        const remaining = Math.max(0, data.duration_minutes * 60 - elapsed);
        setTimeRemaining(remaining);
      } else {
        setTimeRemaining(data.duration_minutes * 60);
      }
      
      setStartTime(Date.now());
      
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error('Failed to start test');
      navigate('/tests');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async () => {
    if (!attempt) return;
    
    try {
      await fetch(`${API_URL}/api/structured-tests/${testId}/save-progress`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answers: answers,
          marked_for_review: markedForReview
        })
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const handleAnswerSelect = (questionIndex, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  const toggleMarkForReview = (questionIndex) => {
    setMarkedForReview(prev => {
      if (prev.includes(questionIndex)) {
        return prev.filter(i => i !== questionIndex);
      } else {
        return [...prev, questionIndex];
      }
    });
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (!autoSubmit) {
      const unanswered = test.questions.length - Object.keys(answers).length;
      if (unanswered > 0) {
        const confirmed = window.confirm(
          `You have ${unanswered} unanswered questions. Are you sure you want to submit?`
        );
        if (!confirmed) return;
      }
    }

    setSubmitting(true);
    try {
      // Convert answers object to array
      const answersArray = test.questions.map((_, idx) => answers[idx] ?? -1);
      const timeTaken = Math.floor((Date.now() - startTime) / 1000);

      const res = await fetch(`${API_URL}/api/structured-tests/${testId}/submit`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answers: answersArray,
          time_taken_seconds: timeTaken
        })
      });

      if (!res.ok) throw new Error('Failed to submit');
      
      const data = await res.json();
      setResults(data);
      setShowResults(true);
      
      if (data.percentage >= 80) {
        toast.success(`Excellent! You scored ${data.percentage}%`);
      } else if (data.percentage >= 60) {
        toast.success(`Good job! You scored ${data.percentage}%`);
      } else {
        toast.info(`You scored ${data.percentage}%. Review your mistakes to improve.`);
      }

    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-500">Loading test...</p>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Score Card */}
          <Card className={`mb-8 ${
            results.percentage >= 80 
              ? 'bg-emerald-50 border-emerald-200' 
              : results.percentage >= 60 
                ? 'bg-yellow-50 border-yellow-200'
                : 'bg-red-50 border-red-200'
          }`}>
            <CardContent className="p-8 text-center">
              <Trophy className={`h-16 w-16 mx-auto mb-4 ${
                results.percentage >= 80 ? 'text-emerald-500' :
                results.percentage >= 60 ? 'text-yellow-500' : 'text-red-500'
              }`} />
              <h1 className="text-4xl font-bold text-slate-900 mb-2">
                {results.score}/{results.total}
              </h1>
              <p className={`text-2xl font-semibold ${
                results.percentage >= 80 ? 'text-emerald-600' :
                results.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {results.percentage}%
                {results.percentage >= 80 ? ' - Excellent!' :
                 results.percentage >= 60 ? ' - Good Job!' : ' - Keep Practicing!'}
              </p>
              
              <div className="flex justify-center gap-6 mt-6 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {Math.floor(results.time_taken_seconds / 60)} min {results.time_taken_seconds % 60} sec
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="h-4 w-4" />
                  {results.mistakes_saved} mistakes saved
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Difficulty Breakdown */}
          <Card className="bg-white border-slate-200 shadow-sm mb-6">
            <CardHeader>
              <CardTitle className="text-slate-900">Performance by Difficulty</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(results.difficulty_scores || {}).map(([diff, data]) => (
                  <div key={diff} className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Badge className={`mb-2 ${
                      diff === 'easy' ? 'bg-green-100 text-green-600' :
                      diff === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {diff}
                    </Badge>
                    <p className="text-2xl font-bold text-slate-900">
                      {data.correct}/{data.total}
                    </p>
                    <p className="text-slate-500 text-sm">
                      {data.total > 0 ? Math.round(data.correct/data.total*100) : 0}%
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Weak Topics */}
          {results.weak_topics && results.weak_topics.length > 0 && (
            <Card className="bg-white border-slate-200 shadow-sm mb-6">
              <CardHeader>
                <CardTitle className="text-orange-600">Topics to Revise</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {results.weak_topics.map((topic, idx) => (
                    <Badge key={idx} variant="outline" className="text-orange-600 border-orange-300">
                      {topic.topic} ({topic.percentage}%)
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Detailed Results */}
          <Card className="bg-white border-slate-200 shadow-sm mb-6">
            <CardHeader>
              <CardTitle className="text-slate-900">Detailed Review</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-h-[500px] overflow-y-auto">
              {results.results.map((r, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    r.is_correct 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {r.is_correct ? (
                      <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-slate-900 font-medium mb-2">Q{idx + 1}. {r.question_text}</p>
                      
                      <div className="space-y-1 text-sm mb-3">
                        {r.options.map((opt, oIdx) => (
                          <div 
                            key={oIdx}
                            className={`px-2 py-1 rounded ${
                              oIdx === r.correct_answer
                                ? 'bg-emerald-100 text-emerald-700'
                                : oIdx === r.user_answer && !r.is_correct
                                  ? 'bg-red-100 text-red-700 line-through'
                                  : 'text-slate-500'
                            }`}
                          >
                            ({String.fromCharCode(65 + oIdx)}) {opt}
                            {oIdx === r.correct_answer && ' ✓'}
                          </div>
                        ))}
                      </div>
                      
                      {!r.is_correct && (
                        <div className="p-3 bg-slate-100 rounded text-sm">
                          <p className="text-slate-600">
                            <span className="font-medium text-slate-700">Explanation: </span>
                            {r.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <Button 
              onClick={() => navigate('/tests')}
              variant="outline"
              className="border-slate-300 text-slate-600"
            >
              Back to Tests
            </Button>
            <Button 
              onClick={() => {
                setShowResults(false);
                setResults(null);
                setAnswers({});
                setMarkedForReview([]);
                startAttempt();
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retake Test
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Test Taking View
  const question = test?.questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = test?.questions.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30">
      {/* Header with Timer */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-4 sticky top-0 z-10 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white font-semibold">{test?.title}</h1>
            <p className="text-emerald-100 text-sm">
              Question {currentQuestion + 1} of {totalQuestions}
            </p>
          </div>
          
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            timeRemaining < 300 ? 'bg-red-500 text-white' : 'bg-white/20 text-white'
          }`}>
            <Clock className="h-5 w-5" />
            <span className="text-xl font-mono font-bold">{formatTime(timeRemaining)}</span>
          </div>
          
          <Button
            onClick={() => handleSubmit()}
            disabled={submitting}
            className="bg-white text-emerald-700 hover:bg-emerald-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Submit Test
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Question Panel */}
        <div className="lg:col-span-3">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center font-bold">
                    {currentQuestion + 1}
                  </span>
                  <div>
                    <Badge variant="outline" className="text-xs text-slate-500 border-slate-300 mr-2">
                      {question?.difficulty}
                    </Badge>
                    <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                      {question?.topic_tag}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleMarkForReview(currentQuestion)}
                  className={markedForReview.includes(currentQuestion) ? 'text-yellow-500' : 'text-slate-400'}
                >
                  {markedForReview.includes(currentQuestion) ? (
                    <BookmarkCheck className="h-5 w-5" />
                  ) : (
                    <Bookmark className="h-5 w-5" />
                  )}
                  Mark for Review
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-900 text-lg mb-6">{question?.question_text}</p>
              
              <div className="space-y-3">
                {question?.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswerSelect(currentQuestion, idx)}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      answers[currentQuestion] === idx
                        ? 'bg-emerald-100 border-emerald-500 text-slate-900'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-white'
                    }`}
                  >
                    <span className="font-medium mr-3">
                      ({String.fromCharCode(65 + idx)})
                    </span>
                    {option}
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestion === 0}
                  className="border-slate-300 text-slate-600"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={() => setCurrentQuestion(prev => Math.min(totalQuestions - 1, prev + 1))}
                  disabled={currentQuestion === totalQuestions - 1}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Question Navigator */}
        <div className="lg:col-span-1">
          <Card className="bg-white border-slate-200 shadow-sm sticky top-24">
            <CardHeader>
              <CardTitle className="text-slate-900 text-sm">Question Navigator</CardTitle>
              <p className="text-slate-500 text-xs">
                {answeredCount}/{totalQuestions} answered
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {test?.questions.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestion(idx)}
                    className={`w-8 h-8 rounded text-xs font-medium transition-all ${
                      currentQuestion === idx
                        ? 'bg-emerald-500 text-white'
                        : answers[idx] !== undefined
                          ? 'bg-emerald-100 text-emerald-600 border border-emerald-300'
                          : markedForReview.includes(idx)
                            ? 'bg-yellow-100 text-yellow-600 border border-yellow-300'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              
              {/* Legend */}
              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-100 border border-emerald-300 rounded"></div>
                  <span className="text-slate-500">Answered</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
                  <span className="text-slate-500">Marked for Review</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-slate-100 rounded"></div>
                  <span className="text-slate-500">Not Answered</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TestAttemptPage;
